import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  IconButton,
  keyframes,
  Switch,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import {
  BudgetPathSelect,
  InputDatePicker,
  type IOption,
  Link,
  NumberInput,
  RemoveButton,
  Select,
  type SelectRef,
  TextField,
} from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  Accounting_Centre_Type,
  ExpenseBudgetItemTypeEnum,
  GetOurWorkBudgetItemsByItemCodeDocument,
  type GetOurWorkBudgetItemsByItemCodeQuery,
  type GetOurWorkBudgetItemsByItemCodeQueryVariables,
  useGetExpenseBudgetItemsByItemCodeLazyQuery,
} from "@src/__generated__/graphql";
import { TIME_BILLING_URL_PARAMS } from "@src/components/modules/projects/time-billing/constants";
import { Icon, TrackedTimeIcon } from "@src/components/ui-kit/Icon";
import { NumberInputPercent } from "@src/components/ui-kit/number-input-percent";
import { SelectManual } from "@src/components/ui-kit/Select/manual";
import { TagSelect } from "@src/components/ui-kit/Select/tag";
import { client } from "@src/services/client";
import { can, cannot } from "@src/utils/components/permissions";
import {
  fieldNumberModelToInputProps,
  fieldToSelectProps,
} from "@src/utils/forms/inputHelpers";
import { useStore as useAppStore } from "@src/utils/hooks";
import { useScreenType } from "@src/utils/hooks/useIsMobile";
import { NumberModel } from "@src/utils/models";
import { TaskSelect } from "@src/widgets/TaskSelect/TaskSelect";
import { UserSelect } from "@src/widgets/UserSelect";
import { format } from "date-fns";
import { kebabCase } from "lodash";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { type ChangeEvent, useEffect, useRef } from "react";
import {
  getColorByStatus,
  getStatusMessage,
} from "../../../listing/table/TableExpenses";
import { getItemIdFromBudgetItemId, type TBudgetItemForm } from "../../form";
import { useFetchProjectsSimple } from "../../hooks";
import { useStore } from "../../store";
import type { BudgetItemStore } from "./BudgetItemStore";
import { PlannedExpenseMappingModal } from "./planned-expense-mapping-modal";

const useSetDefaultAccountingAccount = (
  form: TBudgetItemForm["$"],
  defaultAccountingAccountId: string | undefined,
) => {
  useEffect(() => {
    if (form.accounting_account_id.value || !defaultAccountingAccountId) return;

    form.accounting_account_id.onChange(defaultAccountingAccountId);
  }, [defaultAccountingAccountId]);
};

const ROW_HEIGHT = "30px";

interface BudgetItemProps {
  itemStore: BudgetItemStore;
  id: string;
}

const animation = keyframes`
  0% { background-color: white }
  50% { background-color: var(--chakra-colors-gray-100) }
  100% { background-color: white }
`;

const BudgetItem = ({ id, itemStore }: BudgetItemProps) => {
  const store = useStore();
  const form = itemStore.form.$;
  const appStore = useAppStore();
  const budgetPathSelectRef = useRef<SelectRef>(null);
  const descriptionSelectRef = useRef<SelectRef>(null);
  const showVatSelect =
    appStore.workspaceStore.settings?.show_expense_item_vat_rate ?? false;
  const { isMobile: shouldUseSelectPortal } = useScreenType();

  const canApprove =
    (appStore.authStore.user?.id === form.responsible_id.value ||
      can("expense_approve")) &&
    itemStore.hasCompleteBudgetPath;
  const showSelectBITooltip =
    (!form.responsible_id.$ ||
      form.responsible_id.$ === appStore.authStore.user?.id) &&
    !form.budget_item_id.$ &&
    !form.approved.$;

  const selectedBudgetItemId = computed(() =>
    getItemIdFromBudgetItemId(itemStore.form.$.budget_item_id.$),
  ).get();

  useEffect(() => {
    form.tracked_time.validators(($) => {
      return form.track_hours.value && (!$ || $ <= 0) && t`Value required`;
    });
    form.time_tracking_work_type_id.validators(($) => {
      return form.track_hours.value && !$ && t`Value required`;
    });
    form.task_id.validators(($) => {
      return form.track_hours.value && !$ && t`Value required`;
    });
  }, []);

  useEffect(() => {
    void itemStore.syncPlannedExpenseMappings();
  }, [
    form.budget_item_id.value,
    form.budget_item_type.value,
    store.form.contractor_id.value,
  ]);

  const projectsSimpleLoading = useFetchProjectsSimple({
    brandId: form.brand_id.value,
    projectId: form.project_id.value,
    my_projects: true,
    onCompleted(data) {
      itemStore.setProjectsSimple(data.projectsSimpleMap);
    },
  });

  useSetDefaultAccountingAccount(
    form,
    store.contractor?.defaultAccountingAccount?.id,
  );

  const [fetchBudgetItemByCode, { loading: loadingFetchBudgetItemByCode }] =
    useGetExpenseBudgetItemsByItemCodeLazyQuery({
      onCompleted(data) {
        const expenseBudgetItem = data.expenseBudgetItemsByItemCode[0];

        if (!expenseBudgetItem) return;

        if (
          !!expenseBudgetItem.project.finished_at ||
          !!expenseBudgetItem.project.archived_at
        ) {
          finishedProjectToast();
        } else {
          setBudgetItemFromCodeFetch({
            brandId: expenseBudgetItem.project.brand.id,
            projectId: expenseBudgetItem.project.id,
            budgetItemId: expenseBudgetItem.id,
            responsibleId:
              expenseBudgetItem.project?.project_manager_id ?? undefined,
            budgetItemType: ExpenseBudgetItemTypeEnum.ExpenseBudgetItem,
          });
        }
      },
    });

  const setBudgetItemFromCodeFetch = ({
    brandId,
    projectId,
    budgetItemId,
    responsibleId,
    budgetItemType,
  }: {
    brandId?: string;
    projectId?: string;
    budgetItemId?: string;
    responsibleId?: string;
    budgetItemType: ExpenseBudgetItemTypeEnum;
  }) => {
    if (brandId && brandId !== form.brand_id.value) {
      itemStore.clearProjectsSimple();
    }

    if (projectId && projectId !== form.project_id.value) {
      itemStore.clearSelectedProject();
    }

    brandId && form.brand_id.onChange(brandId);
    projectId && form.project_id.onChange(projectId);
    budgetItemId &&
      form.budget_item_id.onChange(
        store.form.createId(budgetItemId, budgetItemType),
      );
    responsibleId && form.responsible_id.onChange(responsibleId);
    form.budget_item_type.onChange(budgetItemType);
    itemStore.syncApprovalWithBudgetPath();
    budgetPathSelectRef.current?.onMenuClose();
  };

  const fetchOurWorkBudgetItemByCode = async (value: string) => {
    store.isBudgetItemLoadingByCode.on();

    const { data } = await client
      .query<
        GetOurWorkBudgetItemsByItemCodeQuery,
        GetOurWorkBudgetItemsByItemCodeQueryVariables
      >(GetOurWorkBudgetItemsByItemCodeDocument, {
        item_code: value ?? "",
      })
      .toPromise();

    store.isBudgetItemLoadingByCode.off();

    if (!data) return;
    const { ourWorkBudgetItemsByItemCode } = data;

    if (!ourWorkBudgetItemsByItemCode) return;
    const match = ourWorkBudgetItemsByItemCode[0];

    if (!!match.project?.finished_at || !!match.project?.archived_at) {
      finishedProjectToast();
    } else {
      setBudgetItemFromCodeFetch({
        brandId: match.project?.brand.id,
        projectId: match.project?.id,
        responsibleId: match.project?.project_manager_id ?? undefined,
        budgetItemId: match.id,
        budgetItemType: ExpenseBudgetItemTypeEnum.OurWorkBudgetItem,
      });
    }
  };

  const onBudgetItemInputChange = (value: string) => {
    if (value && value.length > 7) {
      if (value.includes("OW")) {
        fetchOurWorkBudgetItemByCode(value);
      } else {
        fetchBudgetItemByCode({
          variables: {
            item_code: value,
          },
        });
      }
    }
  };

  const onResponsibleChange = (value: string) => {
    form.responsible_id.onChange(value);

    if (appStore.workspaceStore.settings?.automatically_approved_expense_items)
      return;

    form.approved.onChange(false);
  };

  const handleApprove = ({ target }: ChangeEvent<HTMLInputElement>) => {
    form.approved.onChange(target.checked);

    if (!target.checked) return;
    if (form.responsible_id.value && form.responsible_id.value.length > 0)
      return;

    form.responsible_id.onChange(appStore.authStore.user?.id);
  };

  const finishedProjectToast = () => {
    appStore.UIStore.toast({
      title: t`Selected project is already finished/archived. Expense cannot be assigned`,
      status: "error",
    });
  };

  const handleVatRateSelect = (value: IOption["value"]) => {
    const matchingRate = store.vatRates.find(
      ({ amount }) => value === amount.toString(),
    );
    form.vatRate.onChange(matchingRate);

    if (!appStore.workspaceStore.settings?.show_accounting_vat_classification)
      return;
    if (!matchingRate) return;

    const matchingClassifications =
      appStore.workspaceStore.accountingVatClassifications.filter(
        ({ vat_rate_id, type, deleted_at }) =>
          vat_rate_id === matchingRate.id &&
          type === Accounting_Centre_Type.Expense &&
          !deleted_at,
      );

    form.accounting_vat_classification_id.onChange(
      matchingClassifications.length === 1
        ? matchingClassifications[0]?.id
        : undefined,
    );
  };

  if (!store.selectedCurrency) {
    return null;
  }

  return (
    <VStack align="start" w="full" spacing="0">
      <Grid
        alignItems="flex-start"
        gap="3"
        templateColumns={store.budgetItemsColumns}
        w="full"
        animation={`${animation} 500ms linear`}
        rounded="md"
      >
        <GridItem colSpan={store.budgetPathSelectColSpan}>
          <Box maxW="full">
            <BudgetPathSelect
              ref={budgetPathSelectRef}
              selectedBrand={itemStore.selectedBrandOption}
              selectedProject={itemStore.selectedProjectOption}
              selectedBudgetItem={itemStore.selectedBudgetItemOption}
              brandOptions={store.brandsOptions}
              projectOptions={itemStore.projectsOptions}
              budgetItemOptions={itemStore.expensesOptions}
              brandError={form.brand_id.error}
              projectError={form.project_id.error}
              budgetItemError={form.budget_item_id.error}
              isLoadingBrands={
                loadingFetchBudgetItemByCode ||
                store.isBudgetItemLoadingByCode.value
              }
              isLoadingProjects={
                projectsSimpleLoading || loadingFetchBudgetItemByCode
              }
              isLoadingBudgetItems={
                itemStore.isLoadingSelectedProject ||
                loadingFetchBudgetItemByCode
              }
              isDisabled={itemStore.isItemClosed}
              asPortal={shouldUseSelectPortal}
              onInputChange={onBudgetItemInputChange}
              onSelectBrand={itemStore.handleSelectBrand}
              onSelectProject={itemStore.handleSelectProject}
              onSelectBudgetItem={itemStore.handleBudgetItemChange}
              onRemoveBrand={itemStore.handleRemoveBrand}
              onRemoveProject={itemStore.handleRemoveProject}
              onRemoveBudgetItem={itemStore.handleRemoveBudgetItemSelection}
            />
          </Box>
        </GridItem>

        <GridItem>
          <Flex align="center" gap="2">
            <NumberInputPercent
              currency
              suffix={store.selectedCurrency.code}
              disabled={itemStore.isItemClosed}
              {...fieldNumberModelToInputProps(form.amount_in_currency)}
              onChange={itemStore.handleAmountChange}
              percentProps={{
                ...fieldNumberModelToInputProps(form.amount_in_percent),
                onChange: itemStore.handlePercentChange,
                isDisabled:
                  itemStore.isItemClosed || itemStore.uninvoicedAmount === 0,
                availableValue: itemStore.uninvoicedAmount,
              }}
              wrapperStyle={{ width: "100%" }}
            />
            {(itemStore.canOpenPlannedExpenseMapping ||
              itemStore.hasPlannedExpenseMappings) && (
              <Tooltip
                hasArrow
                label={
                  itemStore.plannedExpenseMappingStatus === "valid"
                    ? t`Planned expenses mapping is complete`
                    : t`Map this expense item to planned expenses`
                }
                placement="top"
              >
                <IconButton
                  minW="9"
                  px="2"
                  aria-label={t`Planned expenses mapping`}
                  colorScheme={
                    itemStore.plannedExpenseMappingStatus === "valid"
                      ? "green"
                      : "orange"
                  }
                  icon={<Icon name="list" />}
                  isLoading={itemStore.plannedExpenseMappingStore.isLoading}
                  onClick={itemStore.openPlannedExpenseMapping}
                  size="sm"
                  variant={
                    itemStore.plannedExpenseMappingStatus === "valid"
                      ? "outline"
                      : "solid"
                  }
                />
              </Tooltip>
            )}
          </Flex>
        </GridItem>

        <GridItem>
          <UserSelect
            placeholder={t`Responsible`}
            onChange={onResponsibleChange}
            asPortal={shouldUseSelectPortal}
            value={form.responsible_id.value}
            priorityIds={
              appStore.authStore.user ? [appStore.authStore.user.id] : []
            }
          />
        </GridItem>

        <GridItem>
          <Tooltip
            label={
              form.approvals.value.length ? (
                form.approvals.value.map(({ created_at, approved, user }) => (
                  <HStack key={user.full_name + created_at}>
                    <Text>
                      {approved ? t`Approved by` : t`Approval removed by`}{" "}
                      {user.full_name}
                      {" - "}
                      {format(new Date(created_at), "dd.MM.yyyy HH:mm")}
                    </Text>
                  </HStack>
                ))
              ) : showSelectBITooltip ? (
                <Trans>
                  Please fill in all necessary fields to approve this item.
                </Trans>
              ) : (
                <Trans>No one approved this item yet.</Trans>
              )
            }
            placement="top"
          >
            <Flex align="center" justify="center" h={ROW_HEIGHT}>
              <Switch
                isChecked={
                  form.approved.value && itemStore.hasCompleteBudgetPath
                }
                isDisabled={!canApprove}
                onChange={handleApprove}
              />
            </Flex>
          </Tooltip>
        </GridItem>

        {store.hasSomeTrackHours && (
          <GridItem>
            {itemStore.showTrackHours && (
              <Flex align="center" h={ROW_HEIGHT}>
                <Button
                  colorScheme={form.track_hours.value ? "blue" : "grey"}
                  leftIcon={<TrackedTimeIcon />}
                  onClick={() => {
                    itemStore.handleTrackHoursChange(!form.track_hours.value);
                  }}
                  rounded="full"
                  size="xs"
                  variant={form.track_hours.value ? "solid" : "outline"}
                >
                  <Trans>Time</Trans>
                </Button>
              </Flex>
            )}
          </GridItem>
        )}

        {store.hasSomeStatusIcon && (
          <GridItem>
            {itemStore.showStatusIcon && (
              <Tooltip
                label={
                  itemStore.selectedExpenseItem
                    ? getStatusMessage(
                        itemStore.selectedExpenseItem.payment_status,
                      )
                    : undefined
                }
              >
                <Flex align="center" h={ROW_HEIGHT}>
                  <Icon
                    name="currency-euro-circle"
                    color={
                      itemStore.selectedExpenseItem
                        ? getColorByStatus(
                            itemStore.selectedExpenseItem.payment_status,
                          )
                        : undefined
                    }
                  />
                </Flex>
              </Tooltip>
            )}
          </GridItem>
        )}

        {store.hasSomeItemClosed && (
          <GridItem>
            {itemStore.isItemClosed && (
              <Flex align="center" h={ROW_HEIGHT}>
                <Link
                  href={{
                    pathname: "/projects/detail",
                    query: { id: itemStore.selectedProject?.id },
                    hash: kebabCase(itemStore.selectedExpenseItem?.title),
                  }}
                >
                  {t`Budget item is closed`}
                </Link>
              </Flex>
            )}
          </GridItem>
        )}

        <GridItem>
          <HStack align="center" justify="flex-end" h={ROW_HEIGHT} spacing="2">
            {itemStore.isLinkedToLockedTimeBilling && (
              <Tooltip
                hasArrow
                label={t`This expense item is linked to a locked Time Billing calculation`}
                placement="top"
                shouldWrapChildren
              >
                <Link
                  href={{
                    pathname: "/projects/time-billing",
                    query: {
                      id: itemStore.linkedTimeBillingData?.projectId ?? "",
                      [TIME_BILLING_URL_PARAMS.EXPAND]:
                        itemStore.linkedTimeBillingData?.timeBillingId ?? "",
                    },
                  }}
                  target="_blank"
                >
                  <Icon name="lock-01" color="orange.500" />
                </Link>
              </Tooltip>
            )}

            <RemoveButton
              isDisabled={!store.isExpenseDraft && itemStore.isItemClosed}
              onClick={() => {
                store.handleRemoveBudgetItem(id);
                store.fetchExchangeRate();
              }}
            />
          </HStack>
        </GridItem>
      </Grid>
      <Box w="full">
        <Grid
          alignItems="start"
          gap="3"
          templateColumns={store.budgetItemsColumns}
          w="full"
          mx="auto"
          mt="3"
          mb="1"
        >
          <GridItem>
            <Flex justify="flex-end" minH={ROW_HEIGHT} pt="2">
              <Text fontWeight="bold" textAlign="right">
                <strong>
                  <Trans>Accounting:</Trans>
                </strong>
              </Text>
            </Flex>
          </GridItem>
          {showVatSelect && (
            <GridItem>
              <HStack gap="0">
                <Select
                  placeholder={t`VAT %`}
                  value={form.vatRate.value?.amount.toString()}
                  isDisabled={cannot("user_accessAccountingAttributes")}
                  options={store.vatRatesOptionsForBudgetItem}
                  asPortal={shouldUseSelectPortal}
                  onChange={handleVatRateSelect}
                  chakraStyles={{
                    control: (provided) => ({
                      ...provided,
                      borderRightRadius: 0,
                      borderRightWidth: 0,
                      h: `var(--chakra-sizes-9)`,
                      minH: "unset",
                    }),
                  }}
                />
                <NumberInput
                  currency
                  suffix={store.selectedCurrency!.code}
                  decimalDigits={form.vat_amount_in_currency.value.denominator}
                  value={form.vat_amount_in_currency.value.value.toString()}
                  onChange={(val) => {
                    NumberModel.setFieldState(form.vat_amount_in_currency, val);
                  }}
                  disabled={cannot("user_accessAccountingAttributes")}
                  inputStyle={{
                    borderLeftRadius: 0,
                    borderLeftWidth: 0,
                  }}
                />
              </HStack>
            </GridItem>
          )}

          <GridItem>
            <Select
              isDisabled={!store.canUserAccess("set_accounting")}
              placeholder={t`Expense account`}
              asPortal={shouldUseSelectPortal}
              {...fieldToSelectProps(
                form.accounting_account_id,
                store.accountingAccountsOptions,
              )}
            />
          </GridItem>
          <GridItem>
            <Select
              placeholder={t`Cost center`}
              options={itemStore.costCentersOptions}
              asPortal={shouldUseSelectPortal}
              value={form.accounting_centre_id.value}
              onChange={form.accounting_centre_id.onChange}
              error={form.accounting_centre_id.error}
              isDisabled={!store.canUserAccess("set_accounting")}
            />
          </GridItem>
          {appStore.workspaceStore.settings
            ?.show_accounting_vat_classification && (
            <GridItem>
              <Select
                placeholder={t`VAT classification`}
                options={itemStore.vatClassificationsOptions}
                asPortal={shouldUseSelectPortal}
                value={form.accounting_vat_classification_id.value}
                onChange={itemStore.handleVatClassificationChange}
                error={form.accounting_vat_classification_id.error}
                isDisabled={!store.canUserAccess("set_accounting")}
              />
            </GridItem>
          )}
          <GridItem>
            <SelectManual
              ref={descriptionSelectRef}
              allowCreateOnBlur
              asPortal={shouldUseSelectPortal}
              placeholder={t`Description`}
              options={itemStore.descriptionOptions}
              value={form.description_id.value}
              onChange={form.description_id.onChange}
              error={form.description_id.error}
              noOptionsMessage={() => t`No descriptions`}
              isDisabled={!store.canUserAccess("set_accounting")}
              onCreateOption={itemStore.handleCreateNewDescription}
              isClearable
              topExtraContent={
                <Button
                  px="2"
                  py="2"
                  onClick={() => {
                    store.expenseItemDescriptionModalState.onOpen();
                    descriptionSelectRef.current?.onMenuClose();
                  }}
                  size="sm"
                  variant="link"
                >
                  <Trans>Edit descriptions</Trans>
                </Button>
              }
            />
          </GridItem>
          {store.showCostRatio && (
            <GridItem>
              <TextField
                placeholder={t`Cost ratio`}
                value={form.custom_cost_ratio.value}
                onChange={form.custom_cost_ratio.onChange}
                error={form.custom_cost_ratio.error}
                isDisabled={!store.canUserAccess("set_accounting")}
              />
            </GridItem>
          )}
          <GridItem colSpan={store.tagSelectColSpan}>
            <TagSelect
              placeholder={t`Tags`}
              options={store.tagOptions}
              value={form.tag_ids.value}
              onChange={form.tag_ids.onChange}
              error={form.tag_ids.error}
              isLoading={
                appStore.tagStore.isCreating || appStore.tagStore.isLoading
              }
              onCreateOption={itemStore.handleCreateNewTag}
              menuMinWidth="170px"
            />
          </GridItem>
        </Grid>
      </Box>
      {form.track_hours.value && (
        <Box w="full">
          <Grid
            alignItems="start"
            gap="3"
            templateColumns={store.budgetItemsColumns}
            w="full"
            mx="auto"
            mt="3"
            mb="1"
          >
            <GridItem>
              <Flex justify="flex-end" minH={ROW_HEIGHT} pt="2">
                <Text fontWeight="bold" textAlign="right">
                  <strong>
                    <Trans>Track time:</Trans>
                  </strong>
                </Text>
              </Flex>
            </GridItem>
            <GridItem colSpan={2}>
              <TaskSelect
                asPortal={shouldUseSelectPortal}
                withCreateTask={false}
                options={[]}
                labelFormatter={store.taskLabelFormatter}
                projectFilter={form.project_id.$ ? [form.project_id.$] : []}
                budgetItemFilter={
                  selectedBudgetItemId ? [selectedBudgetItemId] : []
                }
                value={form.task_id.value}
                onChange={(val) => {
                  const taskId = Array.isArray(val) ? val?.[0] : val;
                  form.task_id.onChange(taskId);
                  form.time_tracking_work_type_id.reset();
                  itemStore.fetchTaskPositions(taskId);
                }}
              />
            </GridItem>
            <GridItem>
              <NumberInput
                placeholder={t`Hour count`}
                suffix={
                  <Text fontSize="2xs">
                    <Trans>Hours</Trans>
                  </Text>
                }
                error={form.tracked_time.error}
                value={
                  form.tracked_time.value === 0
                    ? ""
                    : form.tracked_time.value.toString()
                }
                onChange={(val) => {
                  form.tracked_time.onChange(Number(val));
                }}
              />
            </GridItem>
            <GridItem>
              <Select
                noOptionsMessage={() => t`Selected task has no positions`}
                placeholder={<Trans>Select position</Trans>}
                asPortal={shouldUseSelectPortal}
                {...fieldToSelectProps(
                  form.time_tracking_work_type_id,
                  itemStore.positionOptions,
                )}
              />
            </GridItem>
            <GridItem colSpan={3}>
              <InputDatePicker
                selected={form.report_date.value}
                onChange={(date) => {
                  form.report_date.onChange(date?.start);
                }}
              />
            </GridItem>
          </Grid>
        </Box>
      )}
      <PlannedExpenseMappingModal itemStore={itemStore} />
    </VStack>
  );
};

export default observer(BudgetItem);
